import { fetchAuthSession } from 'aws-amplify/auth';
import type { AuthSession } from 'aws-amplify/auth';

// Global singleton for session fetching to prevent duplicate Cognito calls
// Both AuthContext and GraphQL client use this to ensure only ONE
// fetchAuthSession call happens at a time
let sessionPromise: Promise<AuthSession> | null = null;
let lastFetchTime = 0;
const MIN_FETCH_INTERVAL = 5000; // Don't refetch within 5 seconds

/**
 * Get the current auth session with deduplication.
 * If a fetch is already in progress, returns the same promise.
 * If we recently fetched (within MIN_FETCH_INTERVAL), uses cached result.
 * This prevents multiple parallel Cognito Identity calls.
 */
export async function getSession(): Promise<AuthSession> {
  const now = Date.now();

  // If we have an in-flight request, return it
  if (sessionPromise) {
    return sessionPromise;
  }

  // Create the session fetch promise
  sessionPromise = fetchAuthSession();
  lastFetchTime = now;

  // Clean up the promise reference after it completes, but keep it long enough
  // for any parallel callers to share it
  sessionPromise
    .finally(() => {
      // Keep the promise around for a bit to handle rapid successive calls
      setTimeout(() => {
        // Only clear if this is still the same promise
        if (Date.now() - lastFetchTime >= MIN_FETCH_INTERVAL) {
          sessionPromise = null;
        }
      }, MIN_FETCH_INTERVAL);
    })
    .catch(() => {
      // On error, clear immediately so retry is possible
      sessionPromise = null;
    });

  return sessionPromise;
}

/**
 * Pre-warm the session/credentials.
 * Call this early to ensure credentials are cached before GraphQL requests.
 */
export async function warmSession(): Promise<void> {
  try {
    await getSession();
  } catch {
    // Ignore errors - actual requests will handle auth failures
  }
}
